import React from 'react';
import {
  Box,
  Breakpoint,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { Form } from 'components/_forms';
import { neutral } from '_lex/_foundations/phxColors';

type ModalProps = {
  actions?: React.ReactNode;
  children: React.ReactNode;
  onClose: () => void;
  open?: boolean;
  size?: false | Breakpoint | undefined;
  title?: string | null;
  subtitle?: string | null;
  onSubmit?: () => void;
  sx?: SxProps<Theme>;
  fullWidthActions?: boolean; // Allow actions to use full width without Stack wrapper
  disableRestoreFocus?: boolean; // Control focus restoration on close
};

function Modal({
  actions = null,
  children = [],
  onClose = () => null,
  open = false,
  size = 'sm',
  title = null,
  subtitle = null,
  onSubmit,
  sx,
  fullWidthActions = false,
  disableRestoreFocus = false,
}: ModalProps) {
  const dialogTitleId = 'modal-dialog-title';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={size}
      scroll='paper'
      sx={sx}
      disableRestoreFocus={disableRestoreFocus}
      aria-labelledby={title ? dialogTitleId : undefined}
    >
      <DialogTitle
        id={dialogTitleId}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 5,
          pr: 1,
          backgroundColor: neutral[50],
          borderBottom: `1px solid ${neutral[100]}`,
        }}
      >
        <Box>
          {subtitle ? (
            <Typography
              variant='eyebrowSm'
              sx={{ color: 'gray', textTransform: 'uppercase', fontWeight: 'bold' }}
            >
              {subtitle}
            </Typography>
          ) : null}
          <Typography variant='subtitleLg'>{title}</Typography>
        </Box>
        <IconButton
          aria-label='close'
          onClick={onClose}
          sx={{ ml: 'auto' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      {onSubmit ? (
        <Form onSubmit={onSubmit}>
          <DialogContent
            sx={{
              maxHeight: '60vh',
              overflow: 'auto',
              display: 'grid',
              gridTemplateRows: '1fr',
            }}
          >
            {children}
          </DialogContent>
          {actions && (
            <DialogActions>
              {fullWidthActions ? (
                <Box sx={{ flex: 1, pt: 3, display: 'flex', width: '100%' }}>{actions}</Box>
              ) : (
                <Stack
                  direction='row'
                  spacing={3}
                  sx={{ flex: 1, pt: 3 }}
                >
                  {actions}
                </Stack>
              )}
            </DialogActions>
          )}
        </Form>
      ) : (
        <>
          <DialogContent
            sx={{
              maxHeight: '60vh',
              overflow: 'auto',
            }}
          >
            {children}
          </DialogContent>
          {actions && (
            <DialogActions
              sx={{
                borderTop: `1px solid ${neutral[100]}`,
                px: 5,
                py: 4,
              }}
            >
              {fullWidthActions ? (
                <Box sx={{ display: 'flex', width: '100%' }}>{actions}</Box>
              ) : (
                <Stack
                  direction='row'
                  spacing={3}
                >
                  {actions}
                </Stack>
              )}
            </DialogActions>
          )}
        </>
      )}
    </Dialog>
  );
}

export default Modal;
